import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_OFFER_SOURCES, LOAN_REQUEST_USAGES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { createId, objectKeys } from 'cpool-utils';
import type { SubmitErrorHandler } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ButtonContent } from './ButtonContent';
import { useRequestOffersMutation } from './hooks/useRequestOffersMutation';
import { useRefinanceAmounts } from './RefinancePlanner/hooks/useRefinanceAmounts';
import { BankOfferWarnings } from './RequestConfiguration/BankOfferWarnings';
import type { SourceTypeObject } from './RequestConfiguration/types';
import type { SchemaCreditRequestForm } from './schemas/creditRequestForm.schema';
import { getButtonClassnames } from './utils/getButtonsClassnames';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useOffersByRequestId } from '~/features/leadDetails/hooks/useOffersByRequestId';
import { getMatchingEuropacePlatformPartnerId } from '~/features/leadDetails/utils/getMatchingEuropacePartnerId';
import useUserInfo from '~/hooks/useUserInfo';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
    updateRequestId: (requestId: string) => void;
    currentRequestId?: string;
};

export const CreditCheckButton: React.FC<Props> = ({ currentRequestId, lead, updateRequestId }) => {
    const { data: offerRequestArray } = useOffersByRequestId({ id: lead.id, requestId: currentRequestId });
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const user = useUserInfo();
    const { permissions } = useAclPermissions({ loanRequestId: lead.id });
    const [isOfferRequestActive, setIsOfferRequestActive] = useState<SourceTypeObject>({
        [LOAN_REQUEST_OFFER_SOURCES.EUROPACE]: false,
    });

    const { mutate: requestOffers } = useRequestOffersMutation({
        queryClient,
        setIsOfferRequestActive,
        updateRequestId,
    });

    const {
        formState: { dirtyFields },
        getValues,
        handleSubmit,
        resetField,
    } = useFormContext<SchemaCreditRequestForm>();
    const { availableCredit } = useRefinanceAmounts(!!lead.coApplicantHidden);

    // NOTE: this check if any credit is changed without saving the form
    const hasUnsavedChanges = objectKeys(dirtyFields).some((key) => key in lead.haushalt.verbindlichkeiten);

    const { partnerId: europacePartnerId } = getMatchingEuropacePlatformPartnerId(
        lead.europace?.platform ?? null,
        user,
    );

    const onFormError: SubmitErrorHandler<SchemaCreditRequestForm> = () => {
        toast.error(t('BANK_CREDIT_REQUEST.FORM_ERRORS'));
    };

    const onSubmit = () => {
        const offerRequestId = createId();
        // NOTE: if the usage is not a car purchase, we reset the car finance values
        if (getValues('verwendungszweck') !== LOAN_REQUEST_USAGES.FAHRZEUGKAUF && !!getValues('carFinance')) {
            resetField('carFinance', { defaultValue: {} });
        }
        requestOffers({ getValues, lead, offerRequestId, user });
    };

    const initiateCreditPossibilityCheck = async () => {
        if (!handleSubmit || europacePartnerId === null) return;
        await handleSubmit(onSubmit, onFormError)();
    };

    const possibilityCheckRunning = Object.values(isOfferRequestActive).some((value) => value === true);

    const requestOffersPermissionDenied = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].requestOffers;
    const creditCheckButtonDisabled =
        availableCredit < 0 ||
        possibilityCheckRunning ||
        europacePartnerId === null ||
        requestOffersPermissionDenied ||
        hasUnsavedChanges;

    return (
        <div className={offerRequestArray ? 'mt-1 rounded-md bg-orange-tag p-3 text-sm text-white' : 'mt-1'}>
            <button
                type="button"
                disabled={creditCheckButtonDisabled}
                className={getButtonClassnames({
                    availableCredit,
                    currentRequestId,
                    disabled: creditCheckButtonDisabled,
                    europacePartnerId,
                    hasUnsavedChanges,
                    possibilityCheckRunning,
                    requestOffersPermissionDenied,
                })}
                onClick={initiateCreditPossibilityCheck}
            >
                <ButtonContent
                    hasUnsavedChanges={hasUnsavedChanges}
                    availableCredit={availableCredit}
                    currentRequestId={currentRequestId}
                    possibilityCheckRunning={possibilityCheckRunning}
                    europacePlatformPartnerId={europacePartnerId}
                    platform={lead.europace?.platform}
                    requestOffersPermissionDenied={requestOffersPermissionDenied}
                />
            </button>
            {currentRequestId && <BankOfferWarnings leadId={lead.id} currentRequestId={currentRequestId} />}
        </div>
    );
};
